import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import {
  Card,
  Container,
  HeaderContainer,
  SubTitle,
  Title,
  TitleContainer,
} from '../../../../../components/v2/Card';
import { StyledTextField, StyledTextFieldContainer } from '../../../../../components/Inputs';
import InputLabel from '../../../../../components/v2/InputLabel';
import SaveButton from '../../../../../components/Button/ActionButtons/SaveButton';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useStorage, { StorageProcessState } from '../../../../../hooks/useStorage';
import { getFileExtension, getFileName } from '../../../../../utils/file';
import { pathPlaceholder, storagePaths } from '../../../../../utils/firebasePaths';
import { isInsideSalesAdmin } from '../../../../../utils/postHog';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useToast from '../../../../hooks/useToast';
import CoachConfig, { LeadConfigOptions } from '../../../../Model/CoachConfig';

import ContactPhotoSetting from './ContactPhotoSetting';
import EndScreenMediaSetting from './EndScreenMediaSetting';
import texts from './texts.json';

/**
 * IS admins set what a coach's qualified leads see after submitting the lead form: the contact card
 * name and photo, and the video or image beside it.
 */
const LeadEndScreenSettings = ({ coachDoc }) => {
  const [coachConfigDoc, setCoachConfigDoc] = useState(null);
  const [contactCardName, setContactCardName] = useState('');
  const [media, setMedia] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isCropOpen, setIsCropOpen] = useState(false);

  const { uploadAttachment } = useStorage();
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { userDoc } = useContext(LoggedInUserContext);
  const isReadOnly = !isInsideSalesAdmin();
  const coachId = coachDoc?.id;

  useEffect(() => {
    const init = async () => {
      setCoachConfigDoc(null);
      if (coachId) {
        try {
          const coachConfigDocument = await CoachConfig.getCoachConfigByCoachId(coachId);
          if (isComponentMountedRef.current) {
            setCoachConfigDoc(coachConfigDocument);
            setContactCardName(coachConfigDocument.contactCardName);
            setMedia(coachConfigDocument.endScreenMedia);
          }
        } catch (error) {
          Sentry.captureException(error, {
            extra: { message: 'Failed to load the lead end screen settings', coachId },
          });
          showToast(texts.errors.load, { error: true });
        }
      }
    };
    init();
  }, [coachId, isComponentMountedRef, showToast]);

  const uploadMedia = useCallback(async ({ url, category, file }) => {
    const storagePath = format(storagePaths.COACH_LEAD_END_SCREEN, { [pathPlaceholder.COACH_ID]: coachId });
    const { state, publicUrl } = await uploadAttachment(
      url,
      getFileExtension(file.name),
      storagePath,
      getFileName(file.name),
      file.type,
      '',
      false,
      setUploadProgress,
    );
    if (state === StorageProcessState.FAILED) {
      throw new Error(texts.errors.save);
    }
    return { url: publicUrl, category };
  }, [coachId, uploadAttachment]);

  const onSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const endScreenMedia = media?.file ? await uploadMedia(media) : media;
      await coachConfigDoc.updateLeadConfig({
        [LeadConfigOptions.CONTACT_CARD_NAME]: contactCardName.trim(),
        [LeadConfigOptions.END_SCREEN_MEDIA]: endScreenMedia,
      }, userDoc.name);
      if (isComponentMountedRef.current) {
        setMedia(endScreenMedia);
        showToast(texts.saved, { type: 'success' });
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { message: 'Failed to save the lead end screen settings', coachId } });
      showToast(texts.errors.save, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
        setUploadProgress(null);
      }
    }
  }, [media, uploadMedia, coachConfigDoc, contactCardName, userDoc, coachId, showToast, isComponentMountedRef]);

  return (
    <Card>
      <HeaderContainer>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.description}</SubTitle>
        </TitleContainer>
      </HeaderContainer>
      {!!coachConfigDoc && (
        <Container>
          <StyledTextFieldContainer $withMargin>
            <InputLabel>{texts.contactCardName}</InputLabel>
            <StyledTextField
              placeholder={coachDoc?.name}
              value={contactCardName}
              onChange={(e) => setContactCardName(e.target.value)}
              helperText={texts.contactCardNameHelper}
              inputProps={{ 'aria-label': texts.contactCardName, maxLength: 40 }}
              disabled={isReadOnly}
            />
          </StyledTextFieldContainer>
          <StyledTextFieldContainer $withMargin>
            <ContactPhotoSetting
              coachConfigDoc={coachConfigDoc}
              coachAvatarUrl={coachDoc?.avatar}
              userName={userDoc?.name}
              isReadOnly={isReadOnly}
              onCropOpenChange={setIsCropOpen}
            />
          </StyledTextFieldContainer>
          <StyledTextFieldContainer $withMargin>
            <EndScreenMediaSetting media={media} onMediaChange={setMedia} isReadOnly={isReadOnly} />
          </StyledTextFieldContainer>
          <SaveButton onClick={onSave} disabled={isReadOnly || isSaving || isCropOpen} $fitToContent>
            {texts.save}
          </SaveButton>
        </Container>
      )}
      <LoadingOverlay
        isLoading={!coachConfigDoc || isSaving}
        loadingText={uploadProgress === null ? '' : format(texts.uploading, { percent: Math.round(uploadProgress) })}
      />
    </Card>
  );
};

LeadEndScreenSettings.propTypes = {
  coachDoc: PropTypes.object,
};

LeadEndScreenSettings.defaultProps = {
  coachDoc: null,
};

export default observer(LeadEndScreenSettings);
